// src/components/admin/UsersTable.tsx
import type { UserUsageRow } from '@/types/admin';

type SortKey = 'lastSeenAt' | 'prompts' | 'tokens' | 'sessions' | 'conversations' | 'files';

interface Props {
  rows: UserUsageRow[];
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
  search: string;
  onSearchChange: (v: string) => void;
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
}

const fmtN = (v: number) => new Intl.NumberFormat('it-IT').format(v);
const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—';

interface ThProps {
  k: SortKey;
  label: string;
  sortBy: SortKey;
  sortDir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}

const Th = ({ k, label, sortBy, sortDir, onSort }: ThProps) => (
  <th
    className="text-left text-xs font-semibold cursor-pointer select-none px-3 py-2"
    onClick={() => onSort(k)}
  >
    {label} {sortBy === k && (sortDir === 'asc' ? '↑' : '↓')}
  </th>
);

export const UsersTable = ({
  rows, sortBy, sortDir, onSort, search, onSearchChange, page, perPage, total, onPageChange,
}: Props) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className="space-y-3">
      <input
        type="search"
        placeholder="Cerca per nome o email"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        className="border rounded px-3 py-1.5 text-sm w-full max-w-sm"
      />
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Nessun utente in questo tenant.</div>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left text-xs font-semibold px-3 py-2">Utente</th>
              <Th k="lastSeenAt" label="Ultimo accesso" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th k="prompts" label="Prompt" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th k="tokens" label="Token" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th k="sessions" label="Sessioni" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th k="conversations" label="Conversazioni" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <Th k="files" label="File" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.userId} className="border-t">
                <td className="px-3 py-2">
                  <div className="font-medium">{r.fullName ?? r.email}</div>
                  <div className="text-xs text-muted-foreground">{r.email} · {r.role}</div>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{fmtDate(r.lastSeenAt)}</td>
                <td className="px-3 py-2 font-mono">{fmtN(r.prompts)}</td>
                <td className="px-3 py-2 font-mono">{fmtN(r.inputTokens + r.outputTokens)}</td>
                <td className="px-3 py-2 font-mono">{fmtN(r.sessions)}</td>
                <td className="px-3 py-2 font-mono">{fmtN(r.conversations)}</td>
                <td className="px-3 py-2 font-mono">{fmtN(r.files)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">Totale: {fmtN(total)}</span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="border rounded px-2 py-1 disabled:opacity-50"
          >
            ← Precedente
          </button>
          <span className="self-center">Pagina {page} di {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            className="border rounded px-2 py-1 disabled:opacity-50"
          >
            Successiva →
          </button>
        </div>
      </div>
    </div>
  );
};
